import { useEffect, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import CiroModal from "../../../shared/CiroModal";
import {
  AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatusQuery,
  AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatusQueryVariables,
  AutopilotSessionWebhookExportModalError_ExportToWebhookTransactionStatusFragmentDoc,
  AutopilotSessionWebhookExportModalLoading_ExportToWebhookTransactionStatusFragmentDoc,
  AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatusFragmentDoc,
  ExportToWebhookProgressStatusEnum,
} from "../../../../__generated__/graphql";
import AutopilotSessionWebhookExportModalForm from "./AutopilotSessionWebhookExportModalForm";
import AutopilotSessionWebhookExportModalLoading, {
  AutopilotSessionWebhookExportModalLoading_ExportToWebhookTransactionStatus,
} from "./AutopilotSessionWebhookExportModalLoading";
import AutopilotSessionWebhookExportModalSuccess, {
  AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatus,
} from "./AutopilotSessionWebhookExportModalSuccess";
import AutopilotSessionWebhookExportModalError, {
  AutopilotSessionWebhookExportModalError_ExportToWebhookTransactionStatus,
} from "./AutopilotSessionWebhookExportModalError";
import { useFragment as getFragmentData } from "../../../../__generated__";

const AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatus = gql`
  query AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatus(
    $transactionId: Int!
  ) {
    exportToWebhookTransactionStatus(transactionId: $transactionId) {
      status
      ...AutopilotSessionWebhookExportModalLoading_ExportToWebhookTransactionStatus
      ...AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatus
      ...AutopilotSessionWebhookExportModalError_ExportToWebhookTransactionStatus
    }
  }
  ${AutopilotSessionWebhookExportModalLoading_ExportToWebhookTransactionStatus}
  ${AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatus}
  ${AutopilotSessionWebhookExportModalError_ExportToWebhookTransactionStatus}
`;

interface IAutopilotSessionWebhookExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  autopilotSessionId: number;
  contactIds: number[] | null;
}

type ModalState = "form" | "loading" | "success" | "error";

const AutopilotSessionWebhookExportModal = ({
  isOpen,
  onClose,
  autopilotSessionId,
  contactIds = null,
}: IAutopilotSessionWebhookExportModalProps) => {
  const [modalState, setModalState] = useState<ModalState>("form");
  const [transactionId, setTransactionId] = useState<number | null>(null);

  const {
    data: statusData,
    startPolling,
    stopPolling,
  } = useQuery<
    AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatusQuery,
    AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatusQueryVariables
  >(AutopilotSessionWebhookExportModal_ExportToWebhookTransactionStatus, {
    variables: { transactionId: transactionId! },
    skip: !transactionId,
  });

  useEffect(() => {
    if (
      statusData?.exportToWebhookTransactionStatus.status ===
      ExportToWebhookProgressStatusEnum.Completed
    ) {
      setModalState("success");
      stopPolling();
    } else if (
      statusData?.exportToWebhookTransactionStatus.status ===
      ExportToWebhookProgressStatusEnum.Failed
    ) {
      setModalState("error");
      stopPolling();
    }
  }, [statusData, stopPolling, setModalState]);

  const handleClose = () => {
    stopPolling();
    setModalState("form");
    setTransactionId(null);
    onClose();
  };

  const handleFormSuccess = (newTransactionId: number) => {
    setTransactionId(newTransactionId);
    setModalState("loading");
    startPolling(1000);
  };

  const renderContent = () => {
    switch (modalState) {
      case "loading":
        return (
          <AutopilotSessionWebhookExportModalLoading
            exportToWebhookTransactionStatus={
              getFragmentData(
                AutopilotSessionWebhookExportModalLoading_ExportToWebhookTransactionStatusFragmentDoc,
                statusData?.exportToWebhookTransactionStatus,
              ) ?? undefined
            }
            onClose={handleClose}
          />
        );
      case "success":
        return (
          <AutopilotSessionWebhookExportModalSuccess
            exportToWebhookTransactionStatus={
              getFragmentData(
                AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatusFragmentDoc,
                statusData?.exportToWebhookTransactionStatus,
              ) ?? undefined
            }
            onClose={handleClose}
          />
        );
      case "error":
        return (
          <AutopilotSessionWebhookExportModalError
            exportToWebhookTransactionStatus={
              getFragmentData(
                AutopilotSessionWebhookExportModalError_ExportToWebhookTransactionStatusFragmentDoc,
                statusData?.exportToWebhookTransactionStatus,
              ) ?? undefined
            }
            onRetry={() => setModalState("form")}
            onClose={handleClose}
          />
        );
      case "form":
      default:
        return (
          <AutopilotSessionWebhookExportModalForm
            autopilotSessionId={autopilotSessionId}
            contactIds={contactIds}
            onSuccess={handleFormSuccess}
            onClose={handleClose}
          />
        );
    }
  };

  return (
    <CiroModal size={"LARGE"} isOpen={isOpen} onClose={handleClose}>
      {renderContent()}
    </CiroModal>
  );
};

export default AutopilotSessionWebhookExportModal;
